import { useEffect, useCallback, memo, type ReactNode } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

export interface BaseFormModalProps {
  open: boolean;
  title?: string;
  theme?: string;
  dialogClass?: string;
  bodyClass?: string;
  saveText?: string;
  cancelText?: string;
  showSave?: boolean;
  showCancel?: boolean;
  saveButtonClass?: string;
  saveDisabled?: boolean;
  scrollLock?: boolean;
  hint?: string;
  subheader?: ReactNode;
  onSave?: () => void;
  onCancel: () => void;
  children?: ReactNode;
}

export const BaseFormModal = memo(function BaseFormModal({
  open,
  title = 'Modal',
  theme: themeProp,
  dialogClass = '',
  bodyClass = '',
  saveText = 'Save',
  cancelText = 'Cancel',
  showSave = true,
  showCancel = true,
  saveButtonClass = 'kstc-btn--primary',
  saveDisabled = false,
  scrollLock = true,
  hint = '',
  subheader,
  onSave,
  onCancel,
  children,
}: BaseFormModalProps) {
  const { theme: globalTheme } = useTheme();
  const theme = themeProp ?? globalTheme;

  // Scroll lock
  useEffect(() => {
    if (!scrollLock) return;
    if (open) {
      document.body.classList.add('kstc-scroll-lock');
    }
    return () => {
      document.body.classList.remove('kstc-scroll-lock');
    };
  }, [open, scrollLock]);

  // Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    },
    [onCancel]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div data-kstc-modal className={`kstc-backdrop ${theme}`}>
      <div
        className={`kstc-dialog ${dialogClass}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="kstc-header">
          <h2 className="kstc-title">{title}</h2>
          <button
            className="kstc-x"
            onClick={onCancel}
            aria-label="Close"
            type="button"
          >
            &times;
          </button>
        </div>

        {subheader}

        <div className={`kstc-body ${bodyClass}`}>
          {hint && <p className="kstc-hint">{hint}</p>}
          {children}
        </div>

        <div className="kstc-actions">
          {showCancel && (
            <button
              className="kstc-btn kstc-btn--secondary"
              onClick={onCancel}
              type="button"
            >
              {cancelText}
            </button>
          )}
          {showSave && (
            <button
              className={`kstc-btn ${saveButtonClass}`}
              onClick={onSave}
              disabled={saveDisabled}
              type="button"
            >
              {saveText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
});
